import React from "react";

import { getCachedMenuData, getCachedStoreData } from "@server/api";
import { DataProvider, usePlasmicQueryData } from "@stadium/services/plasmic";

import { DEFAULT_COUNTRY } from "@stadium/helpers/country/constants";

import { Spinner } from "@stadium/ui/src/components/loaders/loaders";

import { PROMOTIONAL_STORE_SLUG } from "@constants/env-variables";

import { getDynamicSlicesData } from "@helpers/plasmic/store";
import { DATA_PROVIDER_STORE_MENU } from "@helpers/plasmic/slices";

import styles from "./main.module.scss";

type ShopMenuWrapperProps = {
  children: React.ReactNode;
  className: string;
  slug?: string;
  countryCode: string;
};

const ShopMenuWrapper = ({
  children,
  className,
  slug = PROMOTIONAL_STORE_SLUG,
  countryCode,
}: ShopMenuWrapperProps) => {
  const countryIso = countryCode?.toUpperCase() || DEFAULT_COUNTRY.iso;

  const { data: menuAndStoreData, isLoading: isMenuAndStoreDataLoading } = usePlasmicQueryData(
    [DATA_PROVIDER_STORE_MENU, slug, countryIso],
    async () => {
      if (!slug) return { storeData: null, menuData: null };

      const cacheStoreData = await getCachedStoreData(slug);
      const storeData = (cacheStoreData as { store?: any })?.store;

      if (!storeData) return { storeData: null, menuData: null };

      const menuProps = {
        fullMenu: true,
        store: storeData,
        country: { iso: countryIso },
        isNewShopCreationFlow: false,
      };

      const menuResult = await getCachedMenuData(menuProps);
      const menuData = (menuResult as { data?: any })?.data;

      return { storeData, menuData };
    },
  );

  const storeData = menuAndStoreData?.storeData;
  const data = menuAndStoreData?.menuData;

  const dynamicSlicesData = getDynamicSlicesData({
    store: storeData,
    menuData: data,
    countryIso,
  });

  if (isMenuAndStoreDataLoading)
    return (
      <div className={styles.contentLoaderWrapper}>
        <Spinner className={styles.contentLoader} />
      </div>
    );

  return (
    <div className={className}>
      <DataProvider
        name={DATA_PROVIDER_STORE_MENU}
        data={{ countryCode: countryIso, dynamicSlicesData }}
      >
        {children}
      </DataProvider>
    </div>
  );
};

export default ShopMenuWrapper;
